
import { useState, useEffect, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { checkIsAdmin, checkIsSalesAssistant, fetchAssignedBases } from "./utils";
import { SPECIAL_ADMIN_EMAILS } from "./constants";

export const useAuthState = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSalesAssistant, setIsSalesAssistant] = useState(false);
  const [assignedBases, setAssignedBases] = useState<string[]>(['all']);
  const [authInitialized, setAuthInitialized] = useState(false);

  const handleCheckIsAdmin = useCallback(async (): Promise<boolean> => {
    const result = await checkIsAdmin(user);
    setIsAdmin(result);
    return result;
  }, [user]);

  const handleCheckIsSalesAssistant = useCallback(async (): Promise<boolean> => {
    const result = await checkIsSalesAssistant(user);
    setIsSalesAssistant(result);
    return result;
  }, [user]);

  const handleRefreshAssignedBases = useCallback(async (): Promise<void> => {
    const bases = await fetchAssignedBases(user, isAdmin);
    setAssignedBases(bases);
  }, [user, isAdmin]);

  const handleSignOut = useCallback(async () => {
    try {
      console.log("Signing out...");
      
      setIsAdmin(false);
      setIsSalesAssistant(false);
      setAssignedBases(['all']);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Error signing out:", error);
        return Promise.reject(error);
      }
      
      setSession(null);
      setUser(null);
      
      console.log("Successfully signed out");
      
      return Promise.resolve();
    } catch (error) {
      console.error("Error in signOut function:", error);
      return Promise.reject(error);
    }
  }, []);

  const checkAndUpdateUserRoles = useCallback(async (currentUser: User) => {
    if (currentUser.email && SPECIAL_ADMIN_EMAILS.includes(currentUser.email)) {
      console.log("Special admin account detected:", currentUser.email);
      setIsAdmin(true);
      setAssignedBases(['all']);
    } else {
      const adminStatus = await checkIsAdmin(currentUser);
      console.log("Admin status checked:", adminStatus);
      setIsAdmin(adminStatus);
      
      if (adminStatus) {
        setAssignedBases(['all']);
      } else {
        const salesAssistantStatus = await checkIsSalesAssistant(currentUser);
        console.log("Sales Assistant status checked:", salesAssistantStatus);
        setIsSalesAssistant(salesAssistantStatus);
        
        const bases = await fetchAssignedBases(currentUser, adminStatus);
        setAssignedBases(bases);
      }
    }
  }, []);

  return {
    session,
    setSession,
    user,
    setUser,
    loading,
    setLoading,
    isAdmin,
    setIsAdmin,
    isSalesAssistant, 
    setIsSalesAssistant,
    assignedBases,
    setAssignedBases,
    authInitialized,
    setAuthInitialized,
    handleCheckIsAdmin,
    handleCheckIsSalesAssistant,
    handleRefreshAssignedBases,
    handleSignOut,
    checkAndUpdateUserRoles
  };
};
